"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { FormattedPost, Post } from "@/types";
import { PostType } from "@/constants";

type PostContextType = {
  posts: Post[];
  addPost: (post: Partial<Post>) => void;
  clearPosts: () => void;
};

const PostContext = createContext<PostContextType | null>(null);

export function usePostContext() {
  const context = useContext(PostContext);
  if (!context) {
    throw new Error("usePostContext must be used within a PostProvider");
  }
  return context;
}

export function PostProvider({ children }: { children: React.ReactNode }) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Load posts from localStorage on initial render (only on client-side)
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem("posts");

        if (stored) {
          const parsedPosts = JSON.parse(stored);
          if (Array.isArray(parsedPosts)) {
            setPosts(parsedPosts);
          }
        }
      } catch (error) {
        console.error("Error loading posts from localStorage:", error);
      }
      setInitialized(true);
    }
  }, []);

  // Save posts to localStorage whenever they change (but only after initialization)
  useEffect(() => {
    if (typeof window !== "undefined" && initialized) {
      try {
        localStorage.setItem("posts", JSON.stringify(posts));
      } catch (error) {
        console.error("Error saving posts to localStorage:", error);
      }
    }
  }, [posts, initialized]);

  // Add a post with validation to prevent empty posts
  const addPost = (post: Partial<Post>) => {
    if (!post.content?.trim() && (!post.media || post.media.length === 0)) {
      console.error("Cannot add post with empty content");
      return;
    }

    const newPost: Post = {
      id: Date.now().toString(),
      author: {
        display_name: post.author?.display_name,
        id: post.author?.id || "anonymous",
        name: post.author?.name || "Anonymous",
        profile_picture: post.author?.profile_picture,
      },
      username: post.username || "Anonymous",
      handle: post.handle || "anonymous",
      content: post.content || "",
      likes: post.likes || 0,
      comments: post.comments || 0,
      reposts: post.reposts || 0,
      total_reactions: post.total_reactions || 0,
      impressions: post.impressions || 0,
      total_comments: post.total_comments || 0,
      reaction_by_me: post.reaction_by_me,
      timestamp: new Date().toISOString(),
      avatar: post.avatar || "/default-avatar.png",
      type: post.type || PostType.GENERAL,
      location: post.location,
      media: post.media || [],
      links: post.links || [],
      seconds_ago: 0,
      createdAt: new Date().toISOString(),
      mentioned: post.mentioned || [],
      tags: post.tags || [],
      isUserPost: post.isUserPost || false,
      ai_analysis: post.ai_analysis || null,
    };

    setPosts((prevPosts) => [newPost, ...prevPosts]);

    // Update localStorage with simplified media info
    const postsForStorage = [newPost, ...posts].map((post) => ({
      ...post,
      media: post.media?.map((item) => ({
        type: item.type,
        url: item.url,
      })),
    }));

    localStorage.setItem("posts", JSON.stringify(postsForStorage));
  };

  const clearPosts = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("posts");
    }
    setPosts([]);
  };

  return (
    <PostContext.Provider value={{ posts, addPost, clearPosts }}>
      {children}
    </PostContext.Provider>
  );
}
